import React, { createContext, useState, useEffect, useMemo, useRef } from 'react';
import firebase, { db } from 'utils/firebase';
import { forgetSignaturesOfOtherLogin } from 'utils/reservationAuthor';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import LoadingScreen from 'Components/Atoms/LoadingScreen';
import { Restaurant, UserData, UserRoles } from 'utils/types';
import useDocument from 'CustomHooks/useDocument';
import useLoadedFor from 'CustomHooks/useLoadedFor';
import { RestaurantUserDocument } from 'types/shared/documents/restaurants';
import useServerTime from 'CustomHooks/useServerTime';
import { useUndoShortcut } from 'CustomHooks/useUndo';
import { AuthNotice, authNoticeFor } from 'App/Auth/authNotice';
import { clearLocalDrafts } from 'App/Website/localDrafts';
import {
  actionCodeSettingsFor,
  loginPathFor,
  returnPathAfterLogin,
} from 'App/Auth/authReturn';
// import { subscribeUser } from '../serviceWorkerRegistration';

// SSO targets for satellite apps: ?auth=<key> maps to a hardcoded URL.
// Never accept a redirect target from the query string (open redirect —
// it would leak the user's idToken to an attacker-chosen host).
export const EXTERNAL_AUTH_TARGETS: Record<string, string> = {
  'menu-by-gastronaut': 'https://app.menu-by-gastronaut.de/login',
};

const getExternalAuthTarget = () => {
  const auth = new URLSearchParams(location.search).get('auth');
  if (
    !auth ||
    !Object.prototype.hasOwnProperty.call(EXTERNAL_AUTH_TARGETS, auth)
  ) {
    return null;
  }
  return EXTERNAL_AUTH_TARGETS[auth];
};

export type AuthContextType = {
  user: UserType;
  signInUser: (
    method: string,
    providerData?: ProviderDataProps,
  ) => Promise<void>;
  signOutUser: () => Promise<void>;
  retryProfileCheck: () => Promise<void>;
  signUpUser: (method: string, formData?: FormDataProps) => Promise<void>;
  uid: null | string;
  restaurantId: string | null;
  isAdmin?: boolean;
  setrestaurantId: Function;
  loading?: boolean;
  handleForgotPassword: (email: string) => Promise<void>;
  isGastronautAdmin: boolean;
  DateNow: () => number;
  handleChangePassword: (
    oldPwd: string,
    newPwd: string,
  ) => Promise<{
    success: boolean;
    message: string;
  }>;
  roleMap: {
    admin: boolean;
    chefWaiter: boolean;
    waiter: boolean;
    tablet: boolean;
  };
  rolesLoaded: boolean;
  projectId: string;
};
interface AuthContextProps {
  children: any;
  restaurantId: null | string;
  setrestaurantId: Function;
  currentVersion?: string;
}

// interface Restaurant {
//   id: string;
//   role: string;
// }

interface UserType {
  uid: string | null | undefined;
  data: UserData | null | undefined;
  // Explains the last failed or refused sign-in; outlives the sign-out that
  // follows a missing Gastronaut profile.
  notice: AuthNotice | null;
}

export type ProviderDataProps = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export type FormDataProps = {
  email: string;
  password: string;
};

export const AuthContext = createContext<AuthContextType>({
  user: { data: undefined, notice: null, uid: undefined },
  signInUser: async () => {},
  signOutUser: async () => {},
  retryProfileCheck: async () => {},
  signUpUser: async () => {},
  uid: null,
  restaurantId: null,
  isAdmin: false,
  setrestaurantId: () => {},
  handleForgotPassword: async () => {},
  handleChangePassword: (...args: any) =>
    new Promise<{ success: true; message: '' }>((resolve: Function) => {
      setTimeout(() => {
        console.log(args);
        resolve();
      }, 1000);
    }),
  isGastronautAdmin: true,
  roleMap: {
    admin: false,
    chefWaiter: false,
    waiter: true,
    tablet: false,
  },
  rolesLoaded: true,
  DateNow: () => Date.now(),
  projectId: '',
});

const googleProvider = () => {
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.addScope('email');
  // Without it Google silently reuses the last account, which may be one
  // without a Gastronaut profile.
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
};

const providerKind = (currentUser: firebase.User) => {
  const providerId = currentUser.providerData[0]?.providerId;
  if (providerId === 'google.com') return 'google' as const;
  if (providerId === 'password') return 'password' as const;
  return 'other' as const;
};

// After a sign-in on the login page, continue to the page the user came for.
const requestedPath = () =>
  window.location.pathname.startsWith('/auth')
    ? returnPathAfterLogin(window.location.search)
    : window.location.pathname;

const AuthContextProvider = ({
  children,
  restaurantId,
  setrestaurantId,
  currentVersion,
}: AuthContextProps) => {
  const history = useHistory();

  console.log({ currentVersion });

  const [user, setuser] = useState<UserType>({
    uid: undefined,
    data: undefined,
    notice: null,
  });
  const [loading, setloading] = useState(true);
  const sessionRevision = useRef(0);

  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const timeDifference = useServerTime();

  const DateNow = () => Date.now() + timeDifference;

  const signUpUser = async (
    method: string,
    formData?: {
      email: string;
      password: string;
    },
  ): Promise<void> =>
    new Promise((resolve: Function, reject) => {
      let provider: any = null;

      switch (method) {
        case 'email': {
          if (!formData) return resolve();

          const { email, password } = formData;

          return firebase
            .auth()
            .createUserWithEmailAndPassword(email, password)
            .then((res) => resolve())
            .catch(function (error) {
              console.log('error in sign in with email', error);
              resolve();
            });
        }
        case 'google': {
          provider = googleProvider();
          break;
        }
        // Still offered by the onboarding account step.
        case 'facebook': {
          provider = new firebase.auth.FacebookAuthProvider();
          provider.addScope('email');
          break;
        }

        default:
          break;
      }

      firebase
        .auth()
        .signInWithPopup(provider)
        .then((res) => resolve())
        .catch((error) => {
          console.log(error);
          resolve();
        });
    });

  const redirectToExternalTarget = (credential: firebase.auth.UserCredential) => {
    const externalAuthTarget = getExternalAuthTarget();
    if (externalAuthTarget && credential.user) {
      credential.user.getIdToken().then((idToken) => {
        window.location.replace(`${externalAuthTarget}#token=${idToken}`);
      });
    }
  };

  // Resolves once the attempt ended; failures end up as user.notice.
  const signInUser = async (
    method: string,
    providerData?: ProviderDataProps,
  ): Promise<void> => {
    setuser((u) => ({ ...u, notice: null }));

    // Without the tick the session only lives in this tab (SESSION); with it
    // until signing out on this device (LOCAL).
    try {
      await firebase
        .auth()
        .setPersistence(
          providerData?.rememberMe
            ? firebase.auth.Auth.Persistence.LOCAL
            : firebase.auth.Auth.Persistence.SESSION,
        );
    } catch (error) {
      console.log('error setting persistence', error);
      setuser((u) => ({ ...u, notice: { kind: 'persistence-unavailable' } }));
      return;
    }

    try {
      const credential =
        method === 'email'
          ? await firebase
              .auth()
              .signInWithEmailAndPassword(providerData?.email ?? '', providerData?.password ?? '')
          : await firebase.auth().signInWithPopup(googleProvider());
      redirectToExternalTarget(credential);
      // Resolve only after the Gastronaut profile check, so the form stays
      // locked until it is clear whether this account may continue.
      const signedIn = firebase.auth().currentUser;
      if (signedIn) {
        setloading(true);
        await handleUserData(signedIn);
      }
    } catch (error) {
      console.log('error on signIn', error);
      setuser((u) => ({ ...u, notice: authNoticeFor(error) }));
    }
  };

  const signOutUser = () => firebase.auth().signOut();

  const handleUserData = async (currentUser: firebase.User) => {
    const revision = sessionRevision.current;
    const isCurrentSession = () =>
      revision === sessionRevision.current &&
      firebase.auth().currentUser?.uid === currentUser.uid;
    if (!isCurrentSession()) return;
    try {
      await loadUserData(currentUser, isCurrentSession);
    } catch (error) {
      if (!isCurrentSession()) return;
      // Keeps the Firebase session: a tablet must not lose its sign-in over a
      // dropped connection. The login screen offers a retry.
      console.log('error loading the Gastronaut profile', error);
      setuser((u) => ({ ...u, data: null, uid: null, notice: { kind: 'profile-unavailable' } }));
      setloading(false);
    }
  };

  const loadUserData = async (
    currentUser: firebase.User,
    isCurrentSession: () => boolean,
  ) => {
    let displayName = currentUser.displayName;
    let email = currentUser.email;
    let emailVerified = currentUser.emailVerified;
    let photoURL = currentUser.photoURL;
    let isAnonymous = currentUser.isAnonymous;
    let uid = currentUser.uid;
    let provider = currentUser.providerData[0]?.providerId;

    forgetSignaturesOfOtherLogin(uid);

    // window.uid = uid;

    // console.log({ currentUser });

    const userRef = await db.collection('users').doc(uid).get();
    if (!isCurrentSession()) return;

    if (!userRef.exists) {
      //Check for onboarding session
      const onBoardingRef = await db
        .collection('onboardingSessions')
        .where('uid', '==', uid)
        .get();
      if (!isCurrentSession()) return;

      const sessions: any = [];

      onBoardingRef.forEach((doc) =>
        sessions.push({ ...doc.data(), id: doc.id }),
      );
      if (sessions.length) {
        if (
          window.location.href.includes('onboarding') &&
          !window.location.href.includes(
            `onboarding/${sessions[0].tourId}/${sessions[0].id}`,
          )
        ) {
          let conf = window.confirm(
            'Willst du die bestehende Sitzung fortfahren?',
          );

          let data: UserData = {
            displayName,
            email,
            emailVerified,
            photoURL,
            isAnonymous,
            uid,
            role: UserRoles.WAITER,
            restaurants: [],
            // appVersion: currentVersion,
          };
          setuser((u) => ({ ...u, uid, data, notice: null }));

          if (conf) {
            history.push(`/onboarding/${sessions[0].tourId}/${sessions[0].id}`);
            setloading(false);
            return;
          }

          setloading(false);
          return;
        } else {
          history.push(`/onboarding/${sessions[0].tourId}/${sessions[0].id}`);
          let data: UserData = {
            displayName,
            email,
            emailVerified,
            photoURL,
            isAnonymous,
            uid,
            role: UserRoles.WAITER,
            restaurants: [],
            // appVersion: currentVersion,
          };
          setuser((u) => ({ ...u, uid, data, notice: null }));
          setloading(false);
          return;
        }
      }

      // Authenticated, but not a Gastronaut account (e.g. a different Google
      // identity than the invited one). Nothing is created or linked here.
      setuser({
        data: null,
        uid: null,
        notice: { kind: 'profile-missing', provider: providerKind(currentUser), email },
      });
      await signOutUser();
      return;
    }

    let userData = userRef.data();

    let restaurants: any[] = userData?.restaurants || [];
    let appVersion = userData?.appVersion;

    if (!appVersion) appVersion = currentVersion;

    if (userData?.isGastronautAdmin) {
      let allRestaurants: Restaurant[] = [];
      const restaurantsRef = await db
        .collection('restaurantData')
        .where('status', '!=', 'deleted')
        .get();
      if (!isCurrentSession()) return;
      restaurantsRef.forEach((doc) => {
        if (restaurants.find((res) => res.id === doc.id)) return;

        if (doc.data().status === 'deleted') return;

        allRestaurants.push({
          id: doc.id,
          name: displayName || '',
          admin: true,
          role: 'admin',
          title: doc.data()?.name || doc.id,
          products: doc.data()?.productsOwned || [],
        });
      });
      restaurants = [...restaurants, ...allRestaurants];
    }

    let data: UserData = {
      displayName,
      email,
      emailVerified,
      photoURL,
      isAnonymous,
      uid,
      provider,
      role: UserRoles.WAITER,
      ...userData,
      restaurants,
      // appVersion,
    };

    // console.log({ restaurants });

    const targetPath = requestedPath();
    const urlRestaurantId = targetPath.split('/')[1];

    if (
      urlRestaurantId &&
      restaurants.find((r) => r.id === urlRestaurantId)
    ) {
      setrestaurantId(urlRestaurantId);
    } else if (restaurants.length === 0) {
      setrestaurantId(null);
    } else {
      let restaurant =
        restaurantId && restaurants.find((r) => r.id === restaurantId)
          ? restaurantId
          : restaurants[0].id;

      setrestaurantId(restaurant);
    }

    // window.userData = data;
    setuser((u) => ({ ...u, data, uid, notice: null }));
    Sentry.setUser({ id: uid, email: email ?? undefined, username: displayName ?? undefined });
    setloading(false);

    if (window.location.pathname.startsWith('/auth')) {
      history.push(targetPath);
    }
  };

  const retryProfileCheck = async () => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) return;
    setuser((u) => ({ ...u, notice: null }));
    setloading(true);
    await handleUserData(currentUser);
  };


  (window as any).restaurantId = restaurantId;

  useEffect(() => {
    if (restaurantId) {
      Sentry.setTag('restaurantId', restaurantId);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (
      !loading &&
      !user.data &&
      !window.location.pathname.startsWith('/auth') &&
      !window.location.pathname.startsWith('/__/auth/') &&
      !window.location.pathname.startsWith('/onboarding') &&
      !window.location.pathname.startsWith('/loginWithGastronaut')
    ) {
      if (window.location.search.includes('voucherId')) {
        let voucherId = window.location.search.split('voucherId=')[1];

        if (voucherId.includes('&')) {
          voucherId = voucherId.split('&')[0];
        }

        window.location.replace(
          `https://redeem-voucher.vercel.app/${voucherId.replace('_', '/')}`,
        );
      }

      history.push(loginPathFor(window.location.pathname, window.location.search));
    }
  }, [user.data, loading, history]);

  useEffect(() => {
    if (!('data' in user)) {
      let currentUser = firebase.auth().currentUser;

      if (currentUser) {
        handleUserData(currentUser);
      } else {
        setloading(false);
      }
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    let active = true;
    const revision = sessionRevision.current;
    const requestedUid = user.uid;
    if (
      (!loading &&
        !restaurantId &&
        user?.uid &&
        !window.location.href.includes('onboarding') &&
        !window.location.href.includes('auth')) ||
      (restaurantId === 'onboarding' && user?.uid)
    ) {
      db.collection('users')
        .doc(user.uid)
        .get()
        .then((doc) => {
          if (
            !active || revision !== sessionRevision.current ||
            firebase.auth().currentUser?.uid !== requestedUid
          ) return;
          if (doc.exists) {
            let data = doc.data();
            if (data?.restaurants?.length) {
              setrestaurantId(data?.restaurants[0].id);
            } else {
              setrestaurantId(data?.restaurant);
            }
          } else if (window.location.href === '/') {
            signOutUser();
          }
        })
        .catch((err) => {
          console.log(err.message);
        });
    }
    return () => {
      active = false;
    };
  }, [restaurantId, loading, user.uid]);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(function (currentUser) {
      const revision = ++sessionRevision.current;
      setloading(true);
      if (currentUser) {
        setuser((u) =>
          u.uid === currentUser.uid ? u : { ...u, uid: null, data: null }
        );
        handleUserData(currentUser);
        //@TODO logic for subcribePushNotif : if login > get token, if not request for sub

        // subscribeUser(currentUser.uid);
        const externalAuthTarget = getExternalAuthTarget();
        if (externalAuthTarget && currentUser) {
          currentUser.getIdToken().then((idToken) => {
            if (
              revision !== sessionRevision.current ||
              firebase.auth().currentUser?.uid !== currentUser.uid
            ) return;
            window.location.replace(
              `${externalAuthTarget}#token=${idToken}`,
            );
          });
        }
      } else {
        setuser((u) => ({ ...u, uid: null, data: null }));
        setrestaurantId(null);
        Sentry.setUser(null);
        clearLocalDrafts();
        setloading(false);
      }
    });
    return () => {
      sessionRevision.current += 1;
      unsubscribe();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!loading) {
      if (restaurantId && user.data) {
        const currentRestaurant = user.data.restaurants.find(
          (r) => r.id === restaurantId,
        );

        // console.log({ restaurantId, currentRestaurant });

        if (
          !currentRestaurant &&
          !window.location.pathname.startsWith('/__/auth/') &&
          !window.location.pathname.startsWith('/onboarding') &&
          !window.location.pathname.startsWith('/loginWithGastronaut')
        ) {
          history.push('/' + window.location.search);
        }
      }
    }
  }, [restaurantId, loading, history, user.data]);

  const uid = user?.data?.uid || null;

  // The mail leads back to the dashboard host the reset was requested on.
  const handleForgotPassword = async (email: string) =>
    firebase
      .auth()
      .sendPasswordResetEmail(email, actionCodeSettingsFor(window.location.origin));

  const handleChangePassword = async (oldPwd: string, newPwd: string) => {
    let currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      return { success: false, message: 'No current user' };
    }
    const credential = firebase.auth.EmailAuthProvider.credential(
      currentUser.email ?? user.data?.email ?? '',
      oldPwd,
    );
    //Check that the old pwd is the good one
    const answer = await currentUser
      .reauthenticateWithCredential(credential)
      .then(async () => {
        try {
          //Update the new pwd
          await currentUser?.updatePassword(newPwd);
          return { success: true, message: 'Password updated' };
        } catch (error: any) {
          console.log('error on reset password', error);
          return { success: false, message: error.message as string };
        }
      })
      .catch((error: any) => {
        console.log('error on handle change password', error);
        return { success: false, message: 'The actual password is wrong' };
      });

    return answer;
  };

  const isGastronautAdmin = !!user?.data?.isGastronautAdmin;

  const [restaurantUser] = useDocument<RestaurantUserDocument>(
    `/restaurants/${restaurantId}/users`,
    uid,
  );

  const restaurantUserLoaded = useLoadedFor(
    `${restaurantId}/${uid}`,
    restaurantUser.loading,
  );

  const roleMap = useMemo(() => {
    if (isGastronautAdmin) {
      return {
        admin: true,
        waiter: true,
        chefWaiter: true,
        tablet: true,
      };
    }

    if (!restaurantUser?.data) {
      return {
        admin: false,
        waiter: true,
        chefWaiter: false,
        tablet: false,
      };
    }

    return {
      admin: !!restaurantUser.data?.admin,
      waiter: true,
      chefWaiter: !!restaurantUser.data?.chefWaiter,
      tablet: !!restaurantUser.data?.tablet,
    };
  }, [restaurantUser, isGastronautAdmin]);

  useUndoShortcut();

  return (
    <AuthContext.Provider
      value={{
        projectId: process.env?.REACT_APP_FIREBASE_PROJECT_ID || '',
        user,
        signInUser,
        signOutUser,
        retryProfileCheck,
        signUpUser,
        uid,
        restaurantId,
        setrestaurantId,
        isAdmin: roleMap.admin,
        handleForgotPassword,
        loading,
        handleChangePassword,
        isGastronautAdmin,
        roleMap,
        // Until then roleMap says "no role" for everybody.
        rolesLoaded: !loading && (isGastronautAdmin || restaurantUserLoaded),
        DateNow,
      }}
    >
      <LoadingScreen loading={loading} />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
